import { useContext, useState } from 'react';
import axios, { AxiosError } from 'axios';
import type { BaseJobMetadata, User } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import PrimaryButton from '../shared/button/PrimaryButton';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import type { ExportType } from './ExportTypeSelect';
import {
  DATA_JOB_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../util/global_variables';
import { nowISOString } from '../../util/date';
import { getAuthHeaderForUser, handleAuthError } from '../../lib/auth/auth.util';
import { incrementCompanySubscriptionUsage } from '../../graphql/services/subscription.service';
import { isExportQuotaExempt } from '../../lib/app/subscription';

export type ExportRequestParameters = Record<string, unknown>;

export interface ExportParametersProps {
  export_type: ExportType;
}

interface ExportRequestData {
  metadata: BaseJobMetadata;
  data: {
    params: ExportRequestParameters;
  };
}

interface ExportDataButtonProps {
  user: User;
  export_type: ExportType;
  parameters: ExportRequestParameters;
  label?: string;
  disabled?: boolean;
}

const SOURCE = 'ExportDataButton.tsx';

/**
 * Reusable Button to export various kinds of application data via a micro-service cloud function.
 * @param {User} user The user making the export request for metadata.
 * @param {ExportType} export_type Selected export type containing a microservice endpoint and other relevant data.
 * @param {ExportParameters} parameters Dynamic data passed to cloud function endpoint to be used for importing logic.
 * @param {string | undefined} label Optional custom label to use for Button, defaults to Export Data.
 * @param {boolean | undefined} disabled Optionally disable export button based on validation from parent.
 * @returns {JSX.Element}
 */
const ExportDataButton = ({
  user,
  export_type,
  parameters,
  disabled,
  label
}: ExportDataButtonProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { incrementSubscriptionUsage, getExceededSubscriptionKeys } = useSubscriptionContext();
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const isExempt: boolean = isExportQuotaExempt(user);
  const quotaExceeded: boolean =
    !isExempt && getExceededSubscriptionKeys(['monthly_data_exports']).length > 0;

  // Send the export parameter data to the endpoint and handle response.
  const handleExport = async (): Promise<void> => {
    const tracking_id: string = generateUuidV4();
    setExportLoading(true);
    try {
      // Call exporting Cloud Function directly with relevant parameters and metadata.
      const export_request_data: ExportRequestData = {
        metadata: {
          tracking_id: tracking_id,
          key: export_type.id,
          started: nowISOString(),
          requested_by: {
            id: user.id,
            name: `${user.first_name} ${user.last_name}`
          }
        },
        data: {
          params: parameters
        }
      };

      await axios({
        method: 'post',
        url: export_type.endpoint,
        data: export_request_data,
        headers: {
          ...(await getAuthHeaderForUser())
        }
      });

      if (!isExempt) {
        incrementCompanySubscriptionUsage(
          { group_id: group?.id, company_id: company?.id },
          { delta: { monthly_data_exports: 1 } }
        )
          .then(() => incrementSubscriptionUsage(['monthly_data_exports']))
          .catch((err: unknown) => {
            log(
              'error',
              new MetadataError(
                err instanceof Error
                  ? err.message
                  : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleExport'),
                { group_id: group?.id, company_id: company?.id },
                tracking_id
              )
            );
          });
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) handleAuthError(err);
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleExport'),
          {
            user: user,
            export_type: export_type,
            parameters: parameters
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE(DATA_JOB_FAILURE('Export'), tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <PrimaryButton
      sx={{ minWidth: 150, my: 2, py: 1 }}
      onClick={handleExport}
      loadingPosition="center"
      loading={exportLoading}
      disabled={disabled || quotaExceeded}
    >
      <span style={{ visibility: exportLoading ? 'hidden' : 'visible' }}>
        {label ?? 'Export Data'}
      </span>
    </PrimaryButton>
  );
};

export default ExportDataButton;
